import { useId, useLayoutEffect, useRef, useState } from 'react'
import {
  ArrowUpRight,
  Eye,
  Lock,
  Pencil,
  Plus,
  SquareCode,
  X,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { HiddenBadge } from '@/features/snippets/HiddenBadge'
import {
  canCustomize,
  isCareforceSnippet,
  isOutOfDate,
  variantDisplayId,
} from '@/features/snippets/utils'
import type { Snippet } from '@/features/snippets/types'
import {
  SnippetBlockHidden,
  SnippetBlockUnresolved,
} from './SnippetBlockUnresolved'

export type SnippetBlockViewProps = {
  id: string
  /** Body as static HTML from the editor schema; empty when there is none. */
  html: string
  status: 'loading' | 'ready' | 'error'
  snippet: Snippet | undefined
  hidden?: boolean
  interactive: boolean
  /** The duty this script belongs to. Absent outside a duty's script. */
  dutyId?: string
  onEdit?: (snippetId: string, dutyId?: string) => void
  onRemove: () => void
  /** Re-runs a failed fetch. Absent where no query tree backs the source. */
  onRetry?: () => void
}

/**
 * Contents of the snippet block. The panel itself — background, radius, padding
 * — is the NodeView's own element, so ProseMirror sees ONE box and its block
 * spacing lands where it looks like it should.
 *
 * Never renders an empty shell: an author who sees blank assumes the snippet is
 * empty and ships a duty whose call dies terminally. Loading, hidden, missing
 * and failed each say which one they are, and a uuid is only ever shown as a
 * last resort.
 *
 * Inside a duty's script the body is what THAT duty delivers, so a customised
 * snippet shows its own wording and says so. Editing and resetting both happen
 * in the library sheet rather than here: the sheet already owns an editable
 * Milkdown, and a second one nested inside this NodeView would take keystrokes
 * from the surrounding document.
 *
 * A Careforce snippet carries a lock and offers Customize instead of a duty
 * variant: the org's own copy has to exist before a duty can diverge from it.
 */
export function SnippetBlockView({
  id,
  html,
  status,
  snippet,
  hidden = false,
  interactive,
  dutyId,
  onEdit,
  onRemove,
  onRetry,
}: SnippetBlockViewProps) {
  // A row exists only once this duty's wording diverged from the shared body
  // (KD16), so its presence IS what "customised" means.
  const customised = typeof snippet?.duty_content === 'string'
  const careforce = snippet ? isCareforceSnippet(snippet) : false
  const outOfDate = snippet ? isOutOfDate(snippet) : false

  const bodyId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [clipped, setClipped] = useState(false)
  useLayoutEffect(() => {
    if (expanded) return
    const element = bodyRef.current
    // `+ 1` for sub-pixel rounding: without it a body one pixel over offers a
    // "Show all" that expands nothing.
    setClipped(
      element ? element.scrollHeight > element.clientHeight + 1 : false
    )
  }, [html, expanded])

  if (status === 'loading') {
    return (
      <>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-3 h-12 w-full" />
      </>
    )
  }

  if (hidden) return <SnippetBlockHidden />

  if (!snippet) {
    return (
      <SnippetBlockUnresolved
        id={id}
        failedToLoad={status === 'error'}
        interactive={interactive}
        onRemove={onRemove}
        onRetry={onRetry}
      />
    )
  }

  const open = onEdit ? () => onEdit(snippet.id, dutyId) : undefined
  // Same formatter the Variants table uses. The server derives the index so
  // both surfaces name one row; two copies of the format would undo that.
  const variantId =
    snippet.duty_override_index == null
      ? null
      : variantDisplayId(snippet.duty_override_index - 1)

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        {/* Name and variant id read as one label, so they sit in one flex
            child rather than being spread apart by the row's justify. */}
        <span className="flex min-w-0 items-center gap-1">
          <button
            type="button"
            disabled={!open}
            onClick={open}
            className="flex min-w-0 items-center gap-1 text-sm font-medium text-black-alpha-9 enabled:cursor-pointer enabled:hover:text-primary-solid">
            {careforce ? (
              <Lock aria-hidden="true" className="size-4 shrink-0" />
            ) : (
              <SquareCode aria-hidden="true" className="size-4 shrink-0" />
            )}
            <span className="truncate" title={snippet.name}>
              {snippet.name}
            </span>
            <ArrowUpRight aria-hidden="true" className="size-4 shrink-0" />
          </button>
          {careforce && (
            <span
              data-snippet-managed-by=""
              className="shrink-0 text-sm font-medium text-muted-foreground">
              <span aria-hidden="true" className="text-neutral-low-contrast">
                ·
              </span>
              {' Managed by Careforce'}
            </span>
          )}
          {snippet.is_hidden && <HiddenBadge className="shrink-0" />}
          {outOfDate && (
            <Badge
              variant="secondary"
              className="shrink-0"
              title="Careforce updated this snippet after you customized it.">
              Out of date
            </Badge>
          )}
          {customised && (
            <span
              data-snippet-variant-id=""
              className="shrink-0 text-sm font-medium text-muted-foreground">
              <span aria-hidden="true" className="text-neutral-low-contrast">
                ·
              </span>
              {` ${variantId ?? 'Variant'}`}
            </span>
          )}
        </span>
        {interactive && (
          <span className="flex shrink-0 items-center gap-1">
            {open && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-primary-solid hover:text-primary-solid"
                onClick={open}>
                {canCustomize(snippet) ? (
                  <>
                    Customize
                    <Pencil aria-hidden="true" />
                  </>
                ) : careforce ? (
                  <>
                    View
                    <Eye aria-hidden="true" />
                  </>
                ) : !dutyId ? (
                  <>
                    Edit
                    <Pencil aria-hidden="true" />
                  </>
                ) : customised ? (
                  <>
                    Edit variant
                    <Pencil aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Create variant
                    <Plus aria-hidden="true" />
                  </>
                )}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-remove-snippet=""
              aria-label="Remove snippet"
              className="text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 [.snippet-block:focus-within_&]:opacity-100 [.snippet-block:hover_&]:opacity-100"
              onClick={onRemove}>
              <X aria-hidden="true" />
            </Button>
          </span>
        )}
      </div>
      {html ? (
        <div
          id={bodyId}
          ref={bodyRef}
          className={cn(
            'snippet-block__body mt-3 text-sm text-primary-high-contrast',
            !expanded && 'max-h-40 overflow-hidden',
            !expanded && clipped && 'snippet-block__body--clipped'
          )}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Empty snippet</p>
      )}
      {(clipped || expanded) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 text-muted-foreground"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((current) => !current)}>
          {expanded ? 'Show less' : 'Show all'}
        </Button>
      )}
    </>
  )
}
